import { useQuery } from "@tanstack/react-query"
import { BootProgress } from "components/new-stuff/ui/Skeleton"
import { AppBootSkeleton } from "components/new-stuff/layout/AppBootSkeleton"
import { switchedAccount } from "bootMode"
import { ZIndex } from "constants/zIndex"
import AuthModal from "AuthModal"
import MultipleAccountsAlert from "MultipleAccountsAlert"
import axios from "axios"
// import Splashscreen from "Splashscreen"

import { Helmet } from "react-helmet"
import { useAppNotificationStore } from "stores"
import {
  CustomizationSettings,
  useCustomizationSettingsStore,
} from "stores/customization-settings"
import { customizationSettingsBaserUrl } from "utils/constants"
import getAccessibleColor from "utils/getAccessibleColor"
import getRGBColor from "utils/getRGBColor"
import { isPadeDomain } from "utils/utils"

interface Data {
  customization: CustomizationSettings
}
interface IRes {
  data: Data
  success: boolean
}

const AppEntryPoint = ({ children }: { children: React.ReactNode }) => {
  const { setCustomizationSettings, customizationSettings } =
    useCustomizationSettingsStore()
  const { toast } = useAppNotificationStore()
  // const { splashScreen } = useAuthStore()

  const { isFetching, isFetched, data } = useQuery<IRes>({
    queryKey: ["customization-settings"],
    queryFn: async () => {
      // const domain = "payroll.egyptafrica.com"
      const domain = window?.location?.hostname
      const res = await axios.get(
        customizationSettingsBaserUrl + "/v1/integration/" + domain
      )

      return res.data
    },
    refetchOnWindowFocus: false,
    cacheTime: Infinity,
    staleTime: Infinity,
    enabled: isPadeDomain() === false,
    onSuccess: (data) => {
      if (data?.success) {
        localStorage.removeItem("customizationSettings")
        setCustomizationSettings(data?.data?.customization)
      } else {
        // toast.error(data?.message)
      }
    },
  })

  /*
   * The very first thing the app can show, and the hardest to get right.
   *
   * This waits on the white-label settings for the current domain, so it
   * cannot draw branded chrome - the branding is what it is fetching. It also
   * sits in front of the login page, so it cannot draw the app shell either:
   * someone about to see a sign-in form should not glimpse a dashboard frame.
   *
   * Neither placeholder used elsewhere is honest here, and a large centred
   * spinner is what this replaced. A thin bar at the top says work is
   * happening without claiming what is coming. It shows immediately rather
   * than after a delay, because this fetch runs for seconds on a poor
   * connection and a blank screen that long reads as broken - and being
   * peripheral, it does not flash the way a centred spinner would when the
   * fetch is quick.
   *
   * Only white-label domains reach this: the query is disabled when
   * isPadeDomain() is true, and a disabled query never reports fetching.
   */
  // The colours are known before the fetch answers: the settings are kept in local storage
  // from the last visit, so the skeleton below can already be drawn in the customer's brand
  // rather than in a stand-in that changes under them a moment later.
  const primaryColor = getRGBColor(
    customizationSettings.brand.colorScheme,
    "primary"
  )
  const a11yColor = getRGBColor(
    getAccessibleColor(customizationSettings.brand.colorScheme),
    "a11y"
  )

  const brandStyle = (
    <style>
      {`
          :root {
             ${primaryColor};
              ${a11yColor};
          }
        `}
    </style>
  )

  // `isFetching` only turns true once the effect that starts the query has run, so gating on
  // it alone let a frame of the app through first -- content, then a loading screen, for one
  // action. `isFetched` is false from the very first render until the request has settled,
  // one way or the other.
  const brandingUnresolved = isPadeDomain() === false && !isFetched

  if (isFetching || brandingUnresolved) {
    // After an account switch the app shell is honest and already on screen: the person is
    // signed in, they are going back to the dashboard, and the switch drew this very shell
    // before the reload. Drawing the bar instead would make one action show two loaders.
    return (
      <>
        <Helmet>{brandStyle}</Helmet>
        <div
          className="fixed inset-0 bg-white"
          style={{ zIndex: ZIndex.LoadingOverlay }}
        >
          {switchedAccount ? <AppBootSkeleton /> : <BootProgress />}
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>
          {isPadeDomain() === true
            ? "PaidHR - The People Platform"
            : customizationSettings?.brand?.documentTitle}
        </title>
        <meta
          name="description"
          content={customizationSettings?.brand?.documentDescription}
        />
        {customizationSettings?.brand?.faviconUrl ? (
          <link
            rel="icon"
            href={customizationSettings?.brand?.faviconUrl}
          />
        ) : null}
        {brandStyle}
      </Helmet>

      {children}
      <AuthModal />
      <MultipleAccountsAlert />

      {/* {splashScreen && <Splashscreen />} */}
    </>
  )
}

export default AppEntryPoint
